import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { OrderStatus, OrderTypeEnum } from '../../models/orders-type.model';
import { MainStatusCodes } from '../../sections/order-detail/infrastructure/enums/order-status.enums';
import { SubStatusCodes } from '../../sections/order-detail/infrastructure/enums/order-sub-status.enums';
import { getOrderStatus } from '../../sections/order-detail/infrastructure/utils/get-order-status';
import { getSubOrderStatus } from '../../sections/order-detail/infrastructure/utils/get-sub-order-status';
import { DisplayDetailOrder, SetOrderType } from '../../store/orders-type.actions';
import {
    selectDetailOrderId,
    selectOrderType,
} from '../../store/orders-type.selectors';

import { getSubStatusOrder } from './get-sub-status-order';
import { ItemOrderService } from './item-order.service';

import { Order } from '@api/order/order.models';
import { getCmsConfigValue, getLanguageCode } from '@core/infrastructure/state/core.selectors';

@Component({
    selector: 'item-order',
    templateUrl: './item-order.component.html',
    styleUrls: ['./item-order.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemOrderComponent implements OnInit {
    @Input() public order: Partial<Order>;

    @Input() public openedDetail: boolean = false;

    public showDetail$: Observable<boolean> = combineLatest([this.store.select(selectDetailOrderId)
        .pipe(map((expandOrderId) => expandOrderId === this.order.id)), this.store.select(selectOrderType)])
        .pipe(map(([expandOrderId, orderType]) => expandOrderId && orderType === OrderTypeEnum.STORE));

    public image$: Observable<any>;

    public images$: Observable<string[]>;

    public countProducts: number;

    public deliveryDate$: Observable<string>;

    public hasSubOrders$: Observable<boolean>;

    public isCancelable: boolean;

    public languageCode$: Observable<string> = this.store.select(getLanguageCode);

    public orderCurrentStatus: Record<OrderStatus, boolean>;

    public showImageNumber: boolean = true;

    public imageNumber: number = 0;

    public showTooltip: boolean = false;

    public showPointsTooltip: boolean = false;

    public hideDeliveryDates$: Observable<boolean> = this.store.select(getCmsConfigValue('hide_delivery_dates'));

    public currentStatusKey: string;

    constructor(
        private readonly store: Store,
        private readonly itemOrderService: ItemOrderService,
    ) {
    }

    public ngOnInit(): void {
        this.image$ = this.itemOrderService.getImage(this.order);
        const partialOrder = {
            orderTracking: this.order.orderTracking,
            status: this.order.status,
            suborders: this.order.suborders,
        };
        this.deliveryDate$ = this.itemOrderService.getDeliveryDate(partialOrder, null);
        this.countProducts = this.itemOrderService.getCountProducts(this.order);
        this.hasSubOrders$ = this.itemOrderService.hasSubOrders(this.order);
        this.isCancelable = this.itemOrderService.isCancellableOrder(this.order);
        this.orderCurrentStatus = this.itemOrderService.getOrderStatus(this.order);
        this.currentStatusKey = this.getOrderStatusWording();
        if (this.openedDetail) { this.openDetail(); }
        if (this.countProducts > 1) { this.images$ = this.itemOrderService.getImages(this.order); }
        this.showImageNumber = this.countProducts > 4;
        this.imageNumber = this.countProducts - 3;
    }

    private getOrderStatusWording(): string {
        const mainStatusKey = getOrderStatus(this.order.status as MainStatusCodes).groupKey;
        const subStatusCode = getSubStatusOrder(this.order);
        const subStatus = getSubOrderStatus(subStatusCode as SubStatusCodes)?.title;

        return subStatus || mainStatusKey;
    }

    public openDetail() {
        if (this.openedDetail) {
            this.store.dispatch(SetOrderType({ orderType: OrderTypeEnum.STORE }));
        }
        this.store.dispatch(DisplayDetailOrder({ order: this.order, reload: false }));
    }

    public togglePointsTooltip(event) {
        event.stopPropagation();
        this.showPointsTooltip = !this.showPointsTooltip;
    }
}
